import { PatientModel } from "libs/tm-common/src/lib/models/patient.model";
import { OrderModel } from "libs/tm-common/src/lib/models/order.model";
import { Product, ProductLevel, ProductKey } from "libs/product/src/lib/models/product.model";
import { Anamnese, PlanningData } from "libs/anamnese/src/lib/models/anamnese.model";
import { SapJaws } from "libs/sap/src/lib/models/sap.model";
import { UpsPickupReference } from "libs/tm-common/src/lib/models/ups.model";

/** @deprecated only used to query very old treatments in tmadmin*/
export enum TreatmentTypes {
	Prognose = 1,
	Vorbehandlung = 2,
	/** Formerly known as 'Behandlung'*/
	PartnerApiOrder = 3,
	Legacy = 91,
	TmOrder = 40,
	TmAnamnese = 41,
}

export interface TreatmentModel {
	/** Customer WMS-ID of the owning customer. */
	customerWmsId: string;

	status: TreatmentStatus;
	patient: PatientModel;
	product: Product;
	quotation: QuotationModel;
	packages?: ProfessionalTreatmentPackages | FunctionALTreatmentPackages;

	newMessagesCount?: number | 0;
	timeOuts?: TimeOut[];
	created?: Date;
	lastModified?: Date;
	statusChanged?: Date;
	upgrade?: UpgradeModel;
	webviewers?: Webviewer[];
	caseworker?: string;
	completionDate?: Date;
	upsPickupReferences?: UpsPickupReference[];
	readonly schema_version?: number; //will be set by the schema in tm-server
	parentTreatment?: string;
	childTreatments?: string[];
	unreadPublicMessages?: string[];
	distributor?: Distributor;
	additionalQuotations?: Pick<QuotationModel, "docEntry" | "docNum" | "created">[];
	caseAssessmentData?: CaseAssessmentData;

	//exclusive mongodb fields
	_id?: string; 					//ObjectID of mongodb entry

	//non mongodb fields (relevant for redux store)
	needForAction?: number;			//TODO make enum
	neededAction?: string;			//TODO make enum

	/**
	 * @deprecated
	 * legacy with 2.0.0
	 * code still reads attributes from treatments pre 2.0.0
	*/
	orders?: OrderModel[];
	/** @deprecated legacy with 2.3.0 */
	type?: TreatmentTypes;
	/** @deprecated legacy with 2.0.0 */
	name?: string;
	/** @deprecated legacy with 2.0.0 */
	couponCodeForNextOrder?: string;
	// LEGACY fields that may exist on ancient cases (< 2019)
	/** @deprecated last seen on a case: 2019-12-05 */
	anamnese?: any;
	/**@deprecated Internal Magento User-Entity-ID of the owning (customer) user. Do not use. Use customerWmsId instead. */
	uid?: number;
}

export interface QuotationModel {
	docEntry: number, //internal used SAP reference
	docNum: string,   //external used SAP reference
	anamnese?: Anamnese
	ormcoDoctorId?: string,
	ormcoSn?: string,
	bulkOrderItems?: BulkOrderItem[],
	salesOrders?: SalesOrderModel[],
	usedBenefits?: {
		name: string,
		discountedItems?: number, //only applies to benefits with type ITEM_DISCOUNT
	}[],
	created?: Date
}

export interface SalesOrderModel {
	docEntry: number,
	docNum: string,
	created: Date,
	context?: {
		product?: ProductKey | string; //e.g. "pre-aligner"
	}
}

export interface BulkOrderItem{
	patient: string,
	toothRange: string,
	comment: string,
	jaw?: SapJaws
}

export interface UpgradeModel {
	planing: PlanningData,
	packages: ProfessionalTreatmentPackages,
	productKey: ProductKey,

	/**@deprecated */
	product?: Product

	/**@deprecated */
	level?: ProductLevel
}

export class TimeOut{
	type: TimeOutTypes;
	dateTime: Date;
	text?: string;
}

export enum TimeOutTypes {
    FinalTimeout = 'final-timeout',
    DataTransfer = 'data-transfer',
	Reminder = 'reminder',
	MemotainReminder = 'memotain-reminder',
	ReleaseMail = 'release-email',
	ReleaseDashboard = 'release-dashboard',
	AnamneseTimeout = 'anamnese-timeout',
	CaseAssessmentTimeout = 'case-assessment-timeout'
}


export interface ProfessionalTreatmentPackages {
	steps: number[];
	freeCorrections: number[];
	chargeableRefinements?: ChargeableRefinementPackage[];
	next: number;
	plannedRefinementAligners?: ChargeableRefinementPackage;
	/** @deprecated */
	/**
	 * chargeable refinements where saved in this array before 3.2.0,
	 * since then, they are stored in `chargeableRefinements` array
	 */
	addCorrections?: number[]
}

export interface ChargeableRefinementPackage{
	uj: number,
	lj: number
}


export interface FunctionALTreatmentPackages {
	initialPlannedSteps: number;
	pending: FunctionALPackage[];
	active: FunctionALPackage;
	closed: FunctionALPackage[];
	plannedRefinementAligners?: number;
}

export interface FunctionALPackage {
	steps: number,
	type: FunctionALPackageType
}

export enum FunctionALPackageType {
	PLANNED = "planned",
	REFINEMENT = "refinement",
	CHARGEABLE_REFINEMENT = "chargeable_refinement"
}

export enum TreatmentStatus {
	NewRequestReceived = 'new_request_received',
	Planning = 'planning',
	DataReceived = 'data_received',
	RescanImprintsRequired = 'rescan_imprints_required',
	NewImprintsRequested = 'new_imprints_requested',
	NewDataRequested = 'new_data_requested',
	ConsultationRequired = 'consultation_required',
	Shipped = 'shipped',
	Canceled = 'canceled',
	CreditMemoIssued = 'credit_memo_issued',
	Postponed = 'postponed',
	ConsultationComplete = 'consultation_complete',
	ExportRequired = 'export_required',
	InProduction = 'in_production',
	ReleaseRequested = 'release_requested',
	CorrectionRequestReceived = 'correction_request_received',
	Closed = 'closed',
	RequestRejected = 'request_rejected', // "Absage".
	ComplaintReceived = 'complaint_received',
	ComplaintResolved = 'complaint_resolved',
	RefinementRequestReceived = 'refinement_request_received',
	RefinementReleaseRequested = 'refinement_release_requested',
	Goodwill = 'goodwill',
	CaseAssessmentSubmitted = 'case_assessment_treatment_recommended',
}

export enum InterimScanOption {
	WITH_DATA,
	WITHOUT_DATA
}


/** @deprecated*/
export enum TreatmentStati {
	AnamneseRequested = 20,			//XXX Anamnese angefordert
	AnamneseReleaseRequested = 21,  //XXX Warten auf Freigabe
	AnamneseRejected = 23,			//XXX Anamnese zurückgewiesen //wird bei Setup Korrektur gesetzt
	AnamneseCanceld = 29,			//XXX Anamnese abgebrochen
	TreatmentOrdered = 33,			//XXX Behandlung bestellt
	TreatmentRunning = 34,			//XXX Behandlung läuft

	/**
	 * by code unused status
	 * legacy treatments still might have theses status,
	 * but only for display
	 */
	AnamneseReleased = 22,
	AnamneseNotFeasible = 28,
	AnamnseCanceldLegacy = 24,
	PreparationOrdered = 30,
	PreparationRunning = 31,
	ReleaseGranted = 32,
	AwaitingRelease = 40,
	CorrectionRequired = 41,
	Archived= 99,
}

export enum FollowupPackageType {
	Package = 'package',
	ChargeableRefinement = 'chargeableRefinement',
	Refinement = 'refinement'
}

export enum DataTransferMethod{
	UPLOAD = 0,
	UPS = 1,
	OTHER = 2
}

export interface Webviewer {
	name: string;
	url: string;
	visibilityScope: WebviewerVisibilityScope;

	relatedMessages?: string[];
	relatedMedia?: string[];
	relatedModifications?: string[];
}

export enum WebviewerVisibilityScope {
	PUBLIC = "public",
	INTERN = "intern",
	ADMIN = "admin"
}

export enum Distributor {
	AOA = 'aoa',
	ORMCO = 'ormco'
}

export enum CancellationType {
	CUSTOMER = 'customer',
	TIMEOUT = 'timeout',
	GOODWILL = 'goodwill'
}

/**
 * This enum only covers all 'special caseworker'.
 * A caseworker assigned to a treatment can also be a tm admin user (e.g. technician),
 * but in some cases, a "system" caseworker is used for usability.
 */
export enum SpecialCaseWorker {
	CHECK = "CHECK",
	EXPORT = "Export",
	OHNE_PLANUNG = "ohne Planung",
	REKLAMATION = "REKLAMATION",
	REKLAMATION_BEARBEITET = "REKLAMATIONBEARBEITET",
	RUECKSPRACHE = "RUECKSPRACHE",
	STORNO = "Storno"
}

export const SpecialCaseworkerDisplayNameRecord: Record<SpecialCaseWorker, string> = {
	[SpecialCaseWorker.CHECK]: "Check",
	[SpecialCaseWorker.EXPORT]: "Export",
	[SpecialCaseWorker.OHNE_PLANUNG]: "ohne Planung",
	[SpecialCaseWorker.REKLAMATION]: "Reklamation",
	[SpecialCaseWorker.REKLAMATION_BEARBEITET]: "Reklamation bearbeitet",
	[SpecialCaseWorker.RUECKSPRACHE]: "Rücksprache",
	[SpecialCaseWorker.STORNO]: "Storno"
}

export interface CaseAssessmentData {
	recommendedKey: ProductKey;
	uj: boolean;
	lj: boolean;
	comment?: string;
}

export enum ComplaintReason {
	BONDING_NOT_POSSIBLE = "bonding_not_possible",
	FIT_DEFICIENCY = "fit_deficiency",
	FIT_TOO_LOOSE = "fit_too_loose",
	FIT_TOO_TIGHT = "fit_too_tight",
	MATERIAL_BREAKAGE = "material_breakage",
	MISSING_COMPONENT = "missing_component",
	ORDER_NOT_RECEIVED = "order_not_received",
	QUALITY_DEFECT = "quality_defect",
	TRANSPORT_DAMAGE = "transport_damage",
	WRONG_DELIVERY = "wrong_delivery"
}

/** @note keep these in sync with the translations in i18n/de.json and i18n/en.json */
export const ComplaintReasonTextDE: Record<ComplaintReason, string> = {
	[ComplaintReason.BONDING_NOT_POSSIBLE]: "Verklebung nicht möglich",
	[ComplaintReason.FIT_DEFICIENCY]: "Passung mangelhaft",
	[ComplaintReason.FIT_TOO_LOOSE]: "Passung zu locker",
	[ComplaintReason.FIT_TOO_TIGHT]: "Passung zu fest",
	[ComplaintReason.MATERIAL_BREAKAGE]: "Bruch / Materialschaden",
	[ComplaintReason.MISSING_COMPONENT]: "Es fehlt etwas",
	[ComplaintReason.ORDER_NOT_RECEIVED]: "Sendung nicht angekommen",
	[ComplaintReason.QUALITY_DEFECT]: "Qualitätsmangel",
	[ComplaintReason.TRANSPORT_DAMAGE]: "Transportschaden",
	[ComplaintReason.WRONG_DELIVERY]: "Falschlieferung"
};

/** @note keep these in sync with the translations in i18n/de.json and i18n/en.json */
export const ComplaintReasonTextEN: Record<ComplaintReason, string> = {
	[ComplaintReason.BONDING_NOT_POSSIBLE]: "Bonding not possible",
	[ComplaintReason.FIT_DEFICIENCY]: "Poor fit",
	[ComplaintReason.FIT_TOO_LOOSE]: "Too loose fit",
	[ComplaintReason.FIT_TOO_TIGHT]: "Too tight fit",
	[ComplaintReason.MATERIAL_BREAKAGE]: "Material breakage / fracture",
	[ComplaintReason.MISSING_COMPONENT]: "Missing component",
	[ComplaintReason.ORDER_NOT_RECEIVED]: "Order not received",
	[ComplaintReason.QUALITY_DEFECT]: "Quality defect",
	[ComplaintReason.TRANSPORT_DAMAGE]: "Transport damage",
	[ComplaintReason.WRONG_DELIVERY]: "Wrong delivery"
};

export type ExportTreatmentsQueryParams = {
	customerId?: string;
	productFilter?: string[];
	statusFilter?: string[];
	caseworkerFilter?: string[];
	fromDateFilter?: Date;
	toDateFilter?: Date;
	dateType: TreatmentDateType;
};

export enum TreatmentDateType {
	CREATED_DATE = "created",
	LAST_MODIFIED_DATE = "lastModified",
}

export enum NeedForActionAdmin {
	PLAN = 'plan',
	CONTACT = 'contact',
	CHECK = 'check',
	NONE = ''
}

export const NeedForActionStatusRecord: Record<NeedForActionAdmin, TreatmentStatus[]> = {
	[NeedForActionAdmin.PLAN]: [TreatmentStatus.CorrectionRequestReceived, TreatmentStatus.Planning],
	[NeedForActionAdmin.CHECK]: [
		TreatmentStatus.NewRequestReceived,
		TreatmentStatus.DataReceived,
		TreatmentStatus.ComplaintReceived,
	],
	[NeedForActionAdmin.CONTACT]: [TreatmentStatus.ConsultationRequired],
	[NeedForActionAdmin.NONE]: [],
};

export type TreatmentDashboardReportResponse = {
	documentCount: {
		total: number;
		open: number;
		inProduction: number;
		shipped: number;
		cancelled: number;
		withUnreadMessages: number;
	};
	status: Partial<Record<TreatmentStatus, number>>;
	productKeys: Partial<Record<ProductKey, number>>;
	completionDate: {
		today: number;
		tomorrow: number;
		later: number;
	};
	upcomingCancellation: {
		today: number;
		tomorrow: number;
		later: number;
	};
	caseworker: {
		[SpecialCaseWorker.RUECKSPRACHE]: number;
		[SpecialCaseWorker.REKLAMATION]: number;
		[SpecialCaseWorker.STORNO]: number;
	};
	needForAction: {
		[NeedForActionAdmin.PLAN]: number;
		[NeedForActionAdmin.CHECK]: number;
		[NeedForActionAdmin.CONTACT]: number;
	};
};

